import type { CSSProperties } from 'react';
import { ContentLoader } from '@rio-cloud/rio-uikit';
import { useIntl, type IntlShape } from 'react-intl';

import { formatCurrency, formatDate, stages } from '../../config';
import { formatAegAssignTo, formatAegEnum, formatAegStage, type Request, type StageKey } from '../../types';
import { type ColumnKey } from '../ColumnSelector';
import './styles.css';

const SKELETON_KEYS = Array.from({ length: 10 }, (_, index) => `skeleton-${index}`);

export function StagePill({ stage }: { stage: StageKey }) {
    const intl = useIntl();

    return (
        <span
            className='aeg-table-pill text-size-12 text-bold text-color-darkest'
            style={{ '--stage-color': stages[stage].color } as CSSProperties}
        >
            {formatAegStage(intl, stage)}
        </span>
    );
}

type TableProps = {
    rows: Request[];
    onSelect: (id: string) => void;
    visibleColumns: ColumnKey[];
    isLoading?: boolean;
};

const renderCell = (row: Request, column: ColumnKey, intl: IntlShape) => {
    switch (column) {
        case 'id':
            return <td className='aeg-table-mono'>{row.aegNumber || intl.formatMessage({ id: 'intl-msg:support.aeg.card.noNumber' })}</td>;
        case 'client':
            return (
                <td>
                    <b className='display-block text-color-darkest'>{row.client}</b>
                    <small className='display-block text-color-dark'>
                        {formatAegEnum(intl, 'vehicleType', row.vehicleType)}
                    </small>
                </td>
            );
        case 'vehicle':
            return (
                <td>
                    {row.model}
                    <small className='aeg-table-mono display-block text-color-dark'>{row.plate}</small>
                </td>
            );
        case 'chassis':
            return <td className='aeg-table-mono'>{row.chassis}</td>;
        case 'repair':
            return (
                <td className='text-right'>
                    <b className='display-block text-color-darkest'>{formatCurrency(row.value)}</b>
                    <small className='display-block text-color-dark'>
                        {formatAegEnum(intl, 'approvalType', row.approval)}
                    </small>
                </td>
            );
        case 'serviceType':
            return <td>{formatAegEnum(intl, 'serviceType', row.serviceType)}</td>;
        case 'owner':
            return <td>{row.owner}</td>;
        case 'assignedTo':
            return <td>{formatAegAssignTo(intl, row.assignedTo)}</td>;
        case 'stage':
            return (
                <td>
                    <StagePill stage={row.stage} />
                </td>
            );
        case 'regional':
            return <td>{row.regional}</td>;
        // case 'cell':
        //     return <td>{row.cell}</td>;
        case 'dealerDate':
            return (
                <td>
                    {row.dealer}
                    <small className='display-block text-color-dark'>{formatDate(row.date)}</small>
                </td>
            );
        case 'sg':
            return <td className='aeg-table-mono'>{row.sg}</td>;
        case 'repairDate':
            return <td>{formatDate(row.repairDate)}</td>;
        case 'stageTime':
            return <td>{row.stageTime}</td>;
        case 'saleDate':
            return <td>{formatDate(row.saleDate)}</td>;
        case 'km':
            return <td>{row.km.toLocaleString('pt-BR')} km</td>;
        case 'warrantyType':
            return <td>{row.warrantyType}</td>;
    }
};

export default function Table({ rows, onSelect, visibleColumns, isLoading = false }: TableProps) {
    const intl = useIntl();
    const displayedColumns: ColumnKey[] = ['id', ...visibleColumns.filter(column => column !== 'id')];

    return (
        <div className='overflow-auto'>
            <table className='aeg-table-root table table-hover table-head-filled margin-bottom-0 text-color-darkest'>
                <thead className='text-color-dark'>
                    <tr>
                        {displayedColumns.map(column => (
                            <th className={column === 'repair' ? 'text-right' : ''} key={column}>
                                {intl.formatMessage({ id: `intl-msg:support.aeg.columns.${column}` })}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {isLoading
                        ? SKELETON_KEYS.map(key => (
                              <tr key={key}>
                                  {displayedColumns.map(column => (
                                      <td key={column}>
                                          <ContentLoader className='width-100pct' height={16} />
                                      </td>
                                  ))}
                              </tr>
                          ))
                        : rows.map(row => (
                              <tr className='cursor-pointer' key={row.id} onClick={() => onSelect(row.id)}>
                                  {displayedColumns.map(column => (
                                      <TableCell column={column} key={column} row={row} />
                                  ))}
                              </tr>
                          ))}
                </tbody>
            </table>
            {!isLoading && rows.length === 0 ? (
                <div className='padding-30 text-center text-color-dark'>
                    {intl.formatMessage({ id: 'intl-msg:support.aeg.requests.empty' })}
                </div>
            ) : null}
        </div>
    );
}

function TableCell({ row, column }: { row: Request; column: ColumnKey }) {
    const intl = useIntl();
    return renderCell(row, column, intl);
}
